/**
 * scene-difficulty.ts — 读时派生的「决策难度」分级
 *
 * 背景:训练筛选过去用 `scene.level`(视频级难度标签,在素材入库时打在整个视频上),
 * 被误用为「单个决策点的学习者难度」——导致大量干净的单点决策被视频级标签锁在
 * intermediate,新手池被人为抽干(实测 level=beginner 仅 31,而干净决策有 ~299)。
 *
 * 本模块是纯前端派生分级:只读已有的 `scene.decision`(decision-parser 的结构化输出)
 * + `scene.spot.street`,不调 LLM、不碰任何数据文件、不修改 scene 对象、不写回 level。
 * 原 `level` 字段保留作资产出处,随时可回滚。
 *
 * 三档规则(以 decision-parser.ts 实际字段为准):
 *   - beginner(入门):confidence=high 且 frequency∉{mixed,conditional} 且
 *     correct_ids 单一(长度1) 且 gradable=true 且 street∈{preflop,flop}。
 *     ——高置信、单一正解、可计分、前两条街:新手能直接上手。
 *   - advanced(高阶):frequency∈{mixed,conditional} 或 correct_ids 多个。
 *     ——混合策略 / 条件分支 / 多正解,GTO 里本就是高阶概念。
 *   - intermediate(进阶):其余(同等洁净但 street∈{turn,river,multi},或 confidence=medium)。
 *
 * 兜底:decision 缺失 / 未解析时归 intermediate(保守默认:宁可放进进阶池也不误判为
 * 入门污染新手体验,也不误判为高阶把人挡在门外)。
 */

import type { StructuredDecision } from "./decision-parser";
import type { Spot } from "./scene-types";

export type DifficultyGrade = "beginner" | "intermediate" | "advanced";

/** gradeDifficulty 只需决策结构 + 街;PilotScene 天然满足(结构子集,便于测试构造样本)。 */
export type DifficultyInput = {
  decision?: StructuredDecision | null;
  spot: Pick<Spot, "street">;
};

/** 入门街:翻前 / 翻牌。转牌及以后(含多街叙事)对新手认知负担更高。 */
const BEGINNER_STREETS = new Set(["preflop", "flop"]);

/** 高阶频率:混合 / 条件(GTO 混合策略与条件分支天然是进阶内容)。 */
const ADVANCED_FREQUENCY = new Set(["mixed", "conditional"]);

export function gradeDifficulty(scene: DifficultyInput): DifficultyGrade {
  const d = scene.decision;

  // 兜底:无结构化解构 → 保守归进阶(不污染入门,也不误判高阶)。
  if (!d || !d.parsed) return "intermediate";

  // 高阶:混合 / 条件频率,或多正解(混合策略 / 条件分支 / 多正解)。
  if (ADVANCED_FREQUENCY.has(d.frequency) || d.correct_ids.length > 1) {
    return "advanced";
  }

  // 入门:高置信 + 单一正解 + 可计分 + 前两条街。
  if (
    d.confidence === "high" &&
    d.correct_ids.length === 1 &&
    d.gradable &&
    BEGINNER_STREETS.has(scene.spot.street)
  ) {
    return "beginner";
  }

  // 其余:同等洁净但更晚的街,或 confidence=medium。
  return "intermediate";
}
